// ═══════════════════════════════════════════════════════════════════════════
// AdminFeed — the super admin setup screen for the integration feed.
// ═══════════════════════════════════════════════════════════════════════════
// Plan section 6, Phase 1 "React work" (1a setup screen); activation shape (b)
// of plan 7.4; plan 7.5 steps 2, 6, 7, 8 and 12. Reached from the super admin
// sidebar as nav "admin_feed" (App.js), path /admin/feed.
//
// • Hidden unless the server says otherwise: the page renders NOTHING (no
//   spinner, no error) until GET /api/feed/enabled answers 2xx JSON with
//   enabled: true AND super_admin: true (contract 5.1), and nothing when it
//   doesn't. getFeedAccess() never rejects. This only hides the screen;
//   every /api/feed/* route applies its own gates.
// • No hostname checks: one build serves TEST and prod. The webhook URL and
//   header name are shown exactly as the server returns them.
// • All feed data comes from lib/feedApi.js. The account to connect comes
//   from the existing Accounts list ("Set up feed" in App.js AccountsTab hands
//   over { id, name } as prefillAccount), or the admin pastes its ID.
// • Typed days (owner decision 21 Oct 2026): the feed replaces them by default
//   and keeps the replaced rows for restore; the server does that itself.
//   The screen lists them and offers the super admin only "keep typed rows"
//   override (components/FeedConflicts.js).
// • History import (components/FeedImport.js): the Order Transactions Report,
//   parsed in the browser with customer columns dropped, sent in parts to
//   POST /api/feed/connections/{id}/import. Shown once the stores and
//   locations have loaded (the preview routes rows with them), never for a
//   disconnected connection. The super admin may include days on or after a
//   route's feed start (a true-up); they are left out by default.
// • Fully discounted orders are a row like any other: when the API reports a
//   count, the detail shows it as a warning, with the latest channel-days that
//   hold them. Nothing here changes P&L maths.
// • Where the .NET host differs from the contract, this screen follows the
//   host (docs/integrations/FEED_ALIGN_STATUS.md section 1): an import is
//   stored and applied later, so its notice counts no replaced days; a replace
//   that undoes a keep on a day with no feed order moves nothing, so the
//   notice says so and the days reload from the server; a super admin may
//   rotate a token and revoke the one in use at once.
// • Money is in each location's own currency (lib/feedUi.js fmtMoney).
// • No react-router hooks here (AdminView owns navigation), no track() (funnel
//   events are for customers), no alert()/window.confirm, and nothing logged
//   or stored. The one-time token stays inside components/FeedDialogs.js.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../lib/i18n";
import {
  FEED_PROVIDERS, getFeedAccess, adminListConnections, adminSetApproval, getConnection, listStores,
  getMappingTargets, saveMappings, listConflicts, saveDayChoices, pauseConnection, resumeConnection, importOrderChunk,
} from "../lib/feedApi";
import { statusKey, warningKey, warningTone, providerName, fmtDay, fmtDateTime, fmtMoney } from "../lib/feedUi";
import { AdminPage, AdminPageHeader, AdminSectionLabel, AdminCard } from "../components/adminUi";
import {
  inputStyle, adminThStyle, adminTdStyle, codeStyle, btnPrimaryDark, btnSecondary, btnGhostDanger, smallBtn,
  disabledLook, mutedText, emptyRowStyle, tableStyle, theadRowStyle, rowBorder, Banner, Chip, FeedErrorText,
} from "../components/feedKit";
import { CreateConnectionDialog, RotateTokenDialog, ActionDialog } from "../components/FeedDialogs";
import { FeedMapping } from "../components/FeedMapping";
import { FeedConflicts } from "../components/FeedConflicts";
import { FeedImport } from "../components/FeedImport";

// A plug: the page header icon (paths for AdminPageHeader).
export const FEED_ICON_PATHS = ["M12 22v-5", "M9 8V2", "M15 8V2", "M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"];

const CLOSED = Object.freeze({ enabled: false, superAdmin: false, accountId: null, providers: Object.freeze({}) });

// The visibility probe for the super admin feed screens. null while the probe
// is out, then the access object. With active false nothing is sent and the
// answer is closed at once (AdminView passes isSuperAdmin, so other users
// never call /api/feed/enabled for this screen).
export function useFeedAdminAccess(active = true) {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!active) {
      setAccess(CLOSED);
      return undefined;
    }
    let cancelled = false;
    setAccess(null);
    Promise.resolve()
      .then(() => getFeedAccess())
      .then((a) => { if (!cancelled) setAccess(a && typeof a === "object" ? a : CLOSED); })
      .catch(() => { if (!cancelled) setAccess(CLOSED); });
    return () => { cancelled = true; };
  }, [active]);
  return access;
}

// Super admin screens need both flags (contract 5.1).
export function feedAdminVisible(access) {
  return Boolean(access) && access.enabled === true && access.superAdmin === true;
}

// ── App.js navigation gates (AdminView), pure so they can be tested here.
// feedOn: null while the probe is out, then feedAdminVisible's answer. Only a
// strict true shows anything. These hide navigation only: every /api/feed/*
// route applies its own gates.
// The super admin sidebar: the "Integrations" entry (admin_feed) only when on.
export function visibleSuperAdminNav(items, feedOn) {
  return (Array.isArray(items) ? items : []).filter((n) => n.id !== "admin_feed" || feedOn === true);
}
// The Accounts list's "Set up feed" hand-off: the handler only when on.
export function feedSetupHandler(feedOn, handler) {
  return feedOn === true && typeof handler === "function" ? handler : undefined;
}
// Leave /admin/feed only once the probe has said no (null: still asking).
export function leaveFeedScreen(feedOn) {
  return feedOn === false;
}

const IDLE = Object.freeze({ loading: false, error: null, data: null });
const LOADING = Object.freeze({ loading: true, error: null, data: null });
const STATUS_TONE = { pending: "blue", live: "green", paused: "amber", disconnected: "grey" };
const MAX_ZERO_NET_DAYS = 10;   // fully discounted channel-days listed on the detail
const canPause = (c) => c.status === "pending" || c.status === "live";
const canResume = (c) => c.status === "paused";
const asList = (v) => (Array.isArray(v) ? v : []);

function Loading() {
  const { t } = useT();
  return <div style={{ padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 }}>{t("app.shared.loadingEllipsis")}</div>;
}

// Mirrors App.js KV (a label/value row on the admin detail panels).
function KV({ k, children }) {
  return (
    <div style={{ display:"flex", gap:12, padding:"6px 0", borderBottom:"1px dashed #e2e8f0", fontSize:13 }}>
      <div style={{ width:170, flexShrink:0, color:"#94a3b8", fontSize:12, fontWeight:500 }}>{k}</div>
      <div style={{ flex:1, color:"#0f172a", wordBreak:"break-word" }}>{children ?? "—"}</div>
    </div>
  );
}

// A section of the detail panel: label, then a white card.
function Panel({ title, children }) {
  return (
    <div style={{ marginBottom:18 }}>
      <AdminSectionLabel>{title}</AdminSectionLabel>
      <AdminCard style={{ padding:14 }}>{children}</AdminCard>
    </div>
  );
}

// A failed load with a retry, in the admin error style.
function LoadError({ messageKey, error, onRetry }) {
  const { t } = useT();
  return (
    <Banner kind="error">
      <div style={{ fontWeight:600 }}>{t(messageKey)}</div>
      <div><FeedErrorText error={error} /></div>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{ ...smallBtn, marginTop:8 }}>{t("feed.retry")}</button>
      )}
    </Banner>
  );
}

export function AdminFeed({ prefillAccount = null }) {
  const { t } = useT();
  const access = useFeedAdminAccess(true);
  const visible = feedAdminVisible(access);
  const providers = visible ? FEED_PROVIDERS.filter((p) => access.providers && access.providers[p] === true) : [];

  const [list, setList] = useState({ loading: true, error: null, rows: [] });
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [conn, setConn] = useState(IDLE);           // ConnectionDetail
  const [mapping, setMapping] = useState(IDLE);     // { stores, workspaces }
  const [conflicts, setConflicts] = useState(IDLE); // { from, to, conflicts }
  const [mapKey, setMapKey] = useState(0);          // remounts FeedMapping on fresh data
  const [confKey, setConfKey] = useState(0);        // remounts FeedConflicts on a fresh range
  const [dialog, setDialog] = useState(() => (prefillAccount && prefillAccount.id ? { kind: "create", prefill: prefillAccount } : null));
  const [notice, setNotice] = useState(null);       // { key, replacedDays?, heldDays? }
  const req = useRef({ list: 0, conn: 0, mapping: 0, conflicts: 0 });

  // ── loaders: each part keeps only its latest answer ──────────────────────
  const loadList = useCallback(async () => {
    const n = ++req.current.list;
    setList((s) => ({ ...s, loading: true, error: null }));
    try {
      const rows = asList(await adminListConnections());
      if (n === req.current.list) setList({ loading: false, error: null, rows });
    } catch (err) {
      if (n === req.current.list) setList((s) => ({ ...s, loading: false, error: err }));
    }
  }, []);

  const loadConn = useCallback(async (id) => {
    const n = ++req.current.conn;
    setConn((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getConnection(id);
      if (n === req.current.conn) setConn({ loading: false, error: null, data });
    } catch (err) {
      if (n === req.current.conn) setConn((s) => ({ ...s, loading: false, error: err }));
    }
  }, []);

  const loadMapping = useCallback(async (id) => {
    const n = ++req.current.mapping;
    setMapping((s) => ({ ...s, loading: true, error: null }));
    try {
      const [stores, targets] = await Promise.all([listStores(id), getMappingTargets(id)]);
      if (n !== req.current.mapping) return;
      setMapping({ loading: false, error: null, data: { stores: asList(stores), workspaces: asList(targets && targets.workspaces) } });
      setMapKey((k) => k + 1);
    } catch (err) {
      if (n === req.current.mapping) setMapping((s) => ({ ...s, loading: false, error: err }));
    }
  }, []);

  // Rejects when asked to (a range change shows its error in FeedConflicts).
  const loadConflicts = useCallback(async (id, range = {}, rethrow = false) => {
    const n = ++req.current.conflicts;
    setConflicts((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await listConflicts(id, range);
      if (n !== req.current.conflicts) return;
      setConflicts({ loading: false, error: null, data: { from: data.from, to: data.to, conflicts: asList(data.conflicts) } });
      setConfKey((k) => k + 1);
    } catch (err) {
      if (n === req.current.conflicts) setConflicts((s) => ({ ...s, loading: false, error: rethrow ? s.error : err }));
      if (rethrow) throw err;
    }
  }, []);

  useEffect(() => {
    if (visible) loadList();
  }, [visible, loadList]);

  if (!visible) return null;

  // ── selection ────────────────────────────────────────────────────────────
  function openDetail(id) {
    setSelectedId(id);
    setConn(LOADING);
    setMapping(LOADING);
    setConflicts(LOADING);
    loadConn(id);
    loadMapping(id);
    loadConflicts(id);
  }
  function toggleDetail(id) {
    if (selectedId === id) setSelectedId(null);
    else openDetail(id);
  }
  function refreshAll() {
    loadList();
    if (selectedId) {
      loadConn(selectedId);
      loadMapping(selectedId);
      loadConflicts(selectedId, conflicts.data ? { from: conflicts.data.from, to: conflicts.data.to } : {});
    }
  }

  // A connection returned by an action replaces its row at once (the list
  // field storesLiveMonth comes only from the admin list); a refetch follows.
  function mergeRow(c) {
    if (!c || !c.id) return;
    setList((s) => ({
      ...s,
      rows: s.rows.map((r) => (r.id === c.id ? { ...r, ...c, storesLiveMonth: r.storesLiveMonth } : r)),
    }));
  }
  function afterChange(c, key, replacedDays) {
    mergeRow(c);
    setNotice({ key, replacedDays });
    loadList();
    if (c && c.id && selectedId === c.id) loadConn(c.id);
  }

  // ── actions (click handlers only) ────────────────────────────────────────
  async function doPause(c, { reason }) {
    const updated = await pauseConnection(c.id, { reason });
    afterChange(updated || c, "feed.notice.paused");
  }
  async function doResume(c) {
    const res = await resumeConnection(c.id);
    const updated = (res && res.connection) || c;
    afterChange(updated, "feed.notice.resumed", res ? res.replacedDays : 0);
    if (selectedId === c.id) loadConflicts(c.id);
    else openDetail(c.id);
  }
  async function doApproval(c, approved) {
    const updated = await adminSetApproval(c.id, approved);
    afterChange(updated || c, approved ? "feed.notice.approved" : "feed.notice.revoked");
  }
  function onCreated(created) {
    setDialog(null);
    setNotice({ key: "feed.notice.created" });
    loadList();
    if (created && created.id) openDetail(created.id);
  }
  function onRotated(c) {
    setDialog(null);
    setNotice({ key: "feed.notice.rotated" });
    if (selectedId === c.id) loadConn(c.id);
  }
  async function onSaveMapping(payload) {
    const id = selectedId;
    const res = await saveMappings(id, payload);   // a rejection is shown by FeedMapping
    setMapping({ loading: false, error: null, data: { stores: asList(res.stores), workspaces: asList(res.workspaces) } });
    setMapKey((k) => k + 1);
    setNotice({ key: "feed.notice.mappingSaved", replacedDays: res.replacedDays });
    loadConflicts(id, conflicts.data ? { from: conflicts.data.from, to: conflicts.data.to } : {});
    loadConn(id);
    loadList();
  }
  async function onSaveChoices(choices) {
    const id = selectedId;
    const range = conflicts.data ? { from: conflicts.data.from, to: conflicts.data.to } : {};
    const saved = asList(await saveDayChoices(id, choices));   // a rejection is shown by FeedConflicts
    const byKey = new Map(saved.map((s) => [`${s.channelId}\n${s.logDate}`, s]));
    setConflicts((s) => (s.data ? {
      ...s,
      data: {
        ...s.data,
        conflicts: s.data.conflicts.map((c) => {
          const hit = byKey.get(`${c.channelId}\n${c.logDate}`);
          return hit ? { ...c, choice: hit.choice, decidedAt: hit.decidedAt } : c;
        }),
      },
    } : s));
    // Every replace sent undoes a keep. One that replaced nothing found no
    // counting order on its day: the typed rows stay there, locked, until the
    // feed has one (the host's difference 32). The merge above shows the new
    // choice at once, with the day's totals as last loaded; the reload then
    // shows the server's own totals for each day (and the held status).
    const heldDays = saved.filter((s) => s.choice === "replace" && s.replacedCount === 0).length;
    setNotice({ key: "feed.notice.choicesSaved", heldDays });
    loadConflicts(id, range);
    loadConn(id);
    loadList();
  }
  const onConflictRange = (range) => loadConflicts(selectedId, range, true);
  // One request of the history upload; FeedImport sends them one by one.
  const onImportChunk = (rows, timeZone) => importOrderChunk(selectedId, { timeZone, rows });
  // After an upload (or one stopped part way): new stores appear unmapped.
  // The host stores the rows and applies them later (its difference 2), so
  // nothing was replaced yet: the notice counts no days, and the reloads show
  // whatever has applied by then.
  function onImported(_summary, { complete } = {}) {
    const id = selectedId;
    if (complete) setNotice({ key: "feed.notice.imported" });
    loadConn(id);
    loadList();
    loadMapping(id);
    loadConflicts(id, conflicts.data ? { from: conflicts.data.from, to: conflicts.data.to } : {});
  }

  // ── derived ──────────────────────────────────────────────────────────────
  const needle = q.trim().toLowerCase();
  const rows = needle
    ? list.rows.filter((c) => (c.accountName || "").toLowerCase().includes(needle)
      || (c.providerAccountRef || "").includes(needle)
      || (c.accountId || "").toLowerCase().startsWith(needle))
    : list.rows;
  const selectedRow = selectedId ? list.rows.find((r) => r.id === selectedId) || null : null;
  const detail = conn.data && conn.data.id === selectedId ? conn.data : null;
  const head = detail || selectedRow;

  return (
    <AdminPage>
      <AdminPageHeader
        icon={FEED_ICON_PATHS}
        title={t("feed.title")}
        description={t("feed.desc")}
        right={(
          <div style={{ display:"flex", gap:8 }}>
            <button type="button" onClick={refreshAll} style={btnSecondary}>{t("feed.refresh")}</button>
            <button type="button" onClick={() => setDialog({ kind: "create", prefill: null })} disabled={providers.length === 0}
              style={disabledLook(btnPrimaryDark, providers.length === 0)}>{t("feed.new")}</button>
          </div>
        )}
      />

      {notice && (
        <Banner kind="ok" onDismiss={() => setNotice(null)} style={{ marginBottom:14 }}>
          {t(notice.key)}
          {notice.replacedDays > 0 && (
            <> {t("feed.notice.replacedDays")}<b className="num">{notice.replacedDays}</b></>
          )}
          {notice.heldDays > 0 && (
            <> {t("feed.notice.replaceHeld")}<b className="num">{notice.heldDays}</b></>
          )}
        </Banner>
      )}

      <AdminCard>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #f1f5f9", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", background:"#fafbfc" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("feed.list.search")} aria-label={t("feed.list.search")}
            style={{ ...inputStyle, width:"100%", maxWidth:320 }} />
        </div>
        {list.error && (
          <div style={{ padding:"12px 18px" }}>
            <LoadError messageKey="feed.list.loadError" error={list.error} onRetry={loadList} />
          </div>
        )}
        <div style={{ overflowX:"auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr style={theadRowStyle}>
                <th style={adminThStyle}>{t("feed.col.account")}</th>
                <th style={adminThStyle}>{t("feed.col.provider")}</th>
                <th style={adminThStyle}>{t("feed.col.bizId")}</th>
                <th style={adminThStyle}>{t("feed.col.status")}</th>
                <th style={adminThStyle}>{t("feed.col.approval")}</th>
                <th style={adminThStyle}>{t("feed.col.lastEvent")}</th>
                <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.col.stores")}</th>
                <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.col.ordersToday")}</th>
                <th style={adminThStyle}>{t("feed.col.warnings")}</th>
                <th style={adminThStyle}></th>
              </tr>
            </thead>
            <tbody>
              {list.loading && list.rows.length === 0 && (
                <tr><td colSpan={10}><Loading /></td></tr>
              )}
              {!list.loading && !list.error && list.rows.length === 0 && (
                <tr><td colSpan={10} style={emptyRowStyle}>{t("feed.list.empty")}</td></tr>
              )}
              {list.rows.length > 0 && rows.length === 0 && (
                <tr><td colSpan={10} style={emptyRowStyle}>{t("feed.list.noMatch")}</td></tr>
              )}
              {rows.map((c, i) => (
                <tr key={c.id} style={{ ...rowBorder(i), background: c.id === selectedId ? "#f8fafc" : "transparent" }}>
                  <td style={adminTdStyle}>
                    <div style={{ fontWeight:600, color:"#0f172a" }}>{c.accountName || "—"}</div>
                    <div className="num" style={{ fontSize:10.5, color:"#cbd5e1", marginTop:2 }}>{(c.accountId || "").slice(0, 8)}</div>
                  </td>
                  <td style={{ ...adminTdStyle, fontSize:12.5, color:"#475569" }}>{providerName(c.provider)}</td>
                  <td style={adminTdStyle}><code style={codeStyle}>{c.providerAccountRef || "—"}</code></td>
                  <td style={adminTdStyle}>
                    <Chip tone={STATUS_TONE[c.status] || "grey"}>{t(statusKey(c.status))}</Chip>
                    {c.status === "paused" && c.pauseReason && (
                      <div style={{ ...mutedText, marginTop:4, maxWidth:180 }}>{t("feed.list.pauseReason")}{c.pauseReason}</div>
                    )}
                  </td>
                  <td style={adminTdStyle}>
                    <Chip tone={c.approved ? "green" : "amber"}>{c.approved ? t("feed.approval.on") : t("feed.approval.off")}</Chip>
                  </td>
                  <td className="num" style={{ ...adminTdStyle, fontSize:12, color:"#64748b", whiteSpace:"nowrap" }}>
                    {c.lastEventAt ? fmtDateTime(c.lastEventAt) : <span style={mutedText}>{t("feed.list.noEvents")}</span>}
                  </td>
                  <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>
                    <div style={{ fontWeight:600 }}>{c.storesTotal}</div>
                    {c.storesUnmapped > 0 && <div style={{ fontSize:11, color:"#9a6b10", whiteSpace:"nowrap" }}>{t("feed.list.unmapped")}{c.storesUnmapped}</div>}
                    {c.storesLiveMonth != null && <div style={{ ...mutedText, whiteSpace:"nowrap" }}>{t("feed.list.liveMonth")}{c.storesLiveMonth}</div>}
                  </td>
                  <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{c.ordersToday}</td>
                  <td style={{ ...adminTdStyle, maxWidth:240 }}>
                    {c.warnings.length === 0
                      ? <span style={mutedText}>{t("feed.list.noWarnings")}</span>
                      : (
                        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                          {c.warnings.map((w, wi) => <Chip key={`${w}-${wi}`} tone={warningTone(w)}>{t(warningKey(w))}</Chip>)}
                        </div>
                      )}
                  </td>
                  <td style={{ ...adminTdStyle, textAlign:"end" }}>
                    <div style={{ display:"flex", gap:6, justifyContent:"flex-end", flexWrap:"wrap" }}>
                      <button type="button" onClick={() => toggleDetail(c.id)} style={smallBtn}>
                        {c.id === selectedId ? t("feed.action.hide") : t("feed.action.manage")}
                      </button>
                      {canPause(c) && (
                        <button type="button" onClick={() => setDialog({ kind: "pause", connection: c })} style={smallBtn}>{t("feed.action.pause")}</button>
                      )}
                      {canResume(c) && (
                        <button type="button" onClick={() => setDialog({ kind: "resume", connection: c })} style={smallBtn}>{t("feed.action.resume")}</button>
                      )}
                      {!c.approved && c.status !== "disconnected" && (
                        <button type="button" onClick={() => setDialog({ kind: "approve", connection: c })} style={smallBtn}>{t("feed.action.approve")}</button>
                      )}
                      {c.approved && (
                        <button type="button" onClick={() => setDialog({ kind: "revoke", connection: c })} style={btnGhostDanger}>{t("feed.action.revoke")}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      {selectedId && (
        <div style={{ marginTop:26 }}>
          <AdminSectionLabel>
            {(head && head.accountName) || "—"} · {providerName(head && head.provider)} · {t("feed.col.bizId")} {(head && head.providerAccountRef) || "—"}
          </AdminSectionLabel>

          {conn.error && <div style={{ marginBottom:18 }}><LoadError messageKey="feed.detail.loadError" error={conn.error} onRetry={() => loadConn(selectedId)} /></div>}
          {conn.loading && !detail && <AdminCard style={{ marginBottom:18 }}><Loading /></AdminCard>}
          {detail && (
            <ConnectionSummary c={detail}
              onRotate={() => setDialog({ kind: "rotate", connection: detail })}
              onClose={() => setSelectedId(null)}
              workspaces={mapping.data ? mapping.data.workspaces : []} />
          )}

          <Panel title={t("feed.map.title")}>
            {mapping.error && <LoadError messageKey="feed.map.loadError" error={mapping.error} onRetry={() => loadMapping(selectedId)} />}
            {mapping.loading && !mapping.data && <Loading />}
            {mapping.data && (
              <FeedMapping key={`${selectedId}:${mapKey}`} connectionId={selectedId}
                stores={mapping.data.stores} workspaces={mapping.data.workspaces} onSave={onSaveMapping} />
            )}
          </Panel>

          <Panel title={t("feed.conf.title")}>
            {conflicts.error && <LoadError messageKey="feed.conf.loadError" error={conflicts.error} onRetry={() => loadConflicts(selectedId)} />}
            {conflicts.loading && !conflicts.data && <Loading />}
            {conflicts.data && (
              <FeedConflicts key={`${selectedId}:${confKey}`} conflicts={conflicts.data.conflicts}
                from={conflicts.data.from} to={conflicts.data.to}
                workspaces={mapping.data ? mapping.data.workspaces : []}
                onSave={onSaveChoices} onRange={onConflictRange} />
            )}
          </Panel>

          {mapping.data && !(head && head.status === "disconnected") && (
            <Panel title={t("feed.import.title")}>
              <FeedImport key={selectedId} connectionId={selectedId} stores={mapping.data.stores} workspaces={mapping.data.workspaces}
                allowFeedWindow onImportChunk={onImportChunk} onImported={onImported} />
            </Panel>
          )}
        </div>
      )}

      {dialog && dialog.kind === "create" && (
        <CreateConnectionDialog providers={providers} prefill={dialog.prefill} onCancel={() => setDialog(null)} onDone={onCreated} />
      )}
      {dialog && dialog.kind === "rotate" && (
        <RotateTokenDialog connection={dialog.connection} allowRevokePrevious
          onCancel={() => setDialog(null)} onDone={() => onRotated(dialog.connection)} />
      )}
      {dialog && dialog.kind === "pause" && (
        <ActionDialog title={t("feed.pause.title")} body={t("feed.pause.body")} confirmLabel={t("feed.pause.confirm")} withReason
          subject={subjectOf(dialog.connection)} run={(opts) => doPause(dialog.connection, opts)} onClose={() => setDialog(null)} />
      )}
      {dialog && dialog.kind === "resume" && (
        <ActionDialog title={t("feed.resume.title")} body={t("feed.resume.body")} confirmLabel={t("feed.resume.confirm")}
          subject={subjectOf(dialog.connection)} run={() => doResume(dialog.connection)} onClose={() => setDialog(null)} />
      )}
      {dialog && dialog.kind === "approve" && (
        <ActionDialog title={t("feed.approve.title")} body={t("feed.approve.body")} confirmLabel={t("feed.approve.confirm")}
          subject={subjectOf(dialog.connection)} run={() => doApproval(dialog.connection, true)} onClose={() => setDialog(null)} />
      )}
      {dialog && dialog.kind === "revoke" && (
        <ActionDialog title={t("feed.revoke.title")} body={t("feed.revoke.body")} confirmLabel={t("feed.revoke.confirm")} danger
          subject={subjectOf(dialog.connection)} run={() => doApproval(dialog.connection, false)} onClose={() => setDialog(null)} />
      )}
    </AdminPage>
  );
}

function subjectOf(c) {
  return <><b>{c.accountName || "—"}</b> · {providerName(c.provider)} · {c.providerAccountRef}</>;
}

// The connection's detail: state, consent, tokens and today's orders.
function ConnectionSummary({ c, workspaces, onRotate, onClose }) {
  const { t } = useT();
  const wsById = new Map(workspaces.map((w) => [w.id, w]));
  const channelName = (wsId, chId) => {
    const w = wsById.get(wsId);
    const ch = w && chId ? w.channels.find((x) => x.id === chId) : null;
    return ch ? (ch.platform || ch.id) : null;
  };
  // A fully discounted day names its channel only: look in every location.
  const anyChannelName = (chId) => {
    for (const w of workspaces) {
      const ch = (w.channels || []).find((x) => x.id === chId);
      if (ch) return ch.platform || ch.id;
    }
    return null;
  };
  const fullyDiscounted = Number.isFinite(c.fullyDiscountedOrders) ? c.fullyDiscountedOrders : 0;
  const zeroNetDays = Array.isArray(c.fullyDiscountedDays) ? c.fullyDiscountedDays : [];
  return (
    <>
      <AdminCard style={{ padding:14, marginBottom:18 }}>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginBottom:6, flexWrap:"wrap" }}>
          {c.status !== "disconnected" && (
            <button type="button" onClick={onRotate} style={btnSecondary}>{t("feed.action.rotate")}</button>
          )}
          <button type="button" onClick={onClose} style={btnSecondary}>{t("feed.close")}</button>
        </div>
        {fullyDiscounted > 0 && (
          <Banner kind="warn" style={{ marginBottom:10 }}>
            <div style={{ fontWeight:700 }}>{t("feed.detail.fullyDiscountedCount")}<span className="num">{fullyDiscounted}</span></div>
            <div>{t("feed.detail.fullyDiscountedWarn")}</div>
            {zeroNetDays.length > 0 && (
              <div style={{ marginTop:6 }}>
                <div style={{ fontWeight:600 }}>{t("feed.detail.fullyDiscountedDays")}</div>
                <ul style={{ margin:"2px 0 0", paddingInlineStart:18 }}>
                  {zeroNetDays.slice(0, MAX_ZERO_NET_DAYS).map((d, i) => {
                    const name = anyChannelName(d.channelId);
                    return (
                      <li key={`${d.channelId}-${d.logDate}-${i}`}>
                        <span className="num">{fmtDay(d.logDate)}</span>
                        {" · "}{name || <code style={codeStyle}>{d.channelId}</code>}
                        {" · "}{t("feed.detail.dayOrders")}<span className="num">{d.orders}</span>
                      </li>
                    );
                  })}
                </ul>
                {zeroNetDays.length > MAX_ZERO_NET_DAYS && (
                  <div>{t("feed.detail.fullyDiscountedMore")}<span className="num">{zeroNetDays.length - MAX_ZERO_NET_DAYS}</span></div>
                )}
              </div>
            )}
          </Banner>
        )}
        <KV k={t("feed.detail.status")}>
          <Chip tone={STATUS_TONE[c.status] || "grey"}>{t(statusKey(c.status))}</Chip>
          {c.warnings.length > 0 && (
            <span style={{ display:"inline-flex", gap:4, flexWrap:"wrap", marginInlineStart:8 }}>
              {c.warnings.map((w, i) => <Chip key={`${w}-${i}`} tone={warningTone(w)}>{t(warningKey(w))}</Chip>)}
            </span>
          )}
        </KV>
        <KV k={t("feed.detail.approval")}>
          <Chip tone={c.approved ? "green" : "amber"}>{c.approved ? t("feed.approval.on") : t("feed.approval.off")}</Chip>
          {c.approvedAt && <span style={{ ...mutedText, marginInlineStart:8 }}>{fmtDateTime(c.approvedAt)}</span>}
        </KV>
        <KV k={t("feed.detail.consent")}>
          {c.consentTermsVersion
            ? <><code style={codeStyle}>{c.consentTermsVersion}</code> <span style={mutedText}>{fmtDateTime(c.consentedAt)}</span></>
            : <span style={mutedText}>{t("feed.detail.consentNone")}</span>}
        </KV>
        <KV k={t("feed.detail.activation")}>{t("feed.detail.activationAtlas")}</KV>
        <KV k={t("feed.detail.firstEvent")}>{c.firstEventAt ? fmtDateTime(c.firstEventAt) : t("feed.list.noEvents")}</KV>
        <KV k={t("feed.detail.lastEvent")}>{c.lastEventAt ? fmtDateTime(c.lastEventAt) : t("feed.list.noEvents")}</KV>
        <KV k={t("feed.detail.created")}>{fmtDateTime(c.createdAt)}</KV>
        {c.pausedAt && (
          <KV k={t("feed.detail.paused")}>
            {fmtDateTime(c.pausedAt)}{c.pauseReason ? <span style={mutedText}> · {c.pauseReason}</span> : null}
          </KV>
        )}
        <KV k={t("feed.detail.ordersToday")}><span className="num">{c.ordersToday}</span></KV>
        <KV k={t("feed.detail.openOrders")}><span className="num">{c.openOrders}</span></KV>
        <KV k={t("feed.detail.unprocessed")}><span className="num">{c.unprocessedEvents}</span></KV>
        <KV k={t("feed.detail.fullyDiscounted")}><span className="num">{fullyDiscounted}</span></KV>
      </AdminCard>

      <Panel title={t("feed.detail.tokens")}>
        {c.tokens.length === 0 ? (
          <div style={{ ...emptyRowStyle, padding:16 }}>{t("feed.detail.noTokens")}</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={adminThStyle}>{t("feed.detail.tokenCreated")}</th>
                  <th style={adminThStyle}>{t("feed.detail.tokenFirstSeen")}</th>
                  <th style={adminThStyle}>{t("feed.detail.tokenRevoked")}</th>
                </tr>
              </thead>
              <tbody>
                {c.tokens.map((tok, i) => (
                  <tr key={tok.id} style={rowBorder(i)}>
                    <td className="num" style={adminTdStyle}>{fmtDateTime(tok.createdAt)}</td>
                    <td className="num" style={adminTdStyle}>{tok.firstSeenAt ? fmtDateTime(tok.firstSeenAt) : <span style={mutedText}>{t("feed.detail.tokenNotSeen")}</span>}</td>
                    <td className="num" style={adminTdStyle}>
                      {tok.revokedAt ? fmtDateTime(tok.revokedAt) : <Chip tone="green">{t("feed.detail.tokenActive")}</Chip>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title={t("feed.detail.today")}>
        {c.todayByPlatform.length === 0 ? (
          <div style={{ ...emptyRowStyle, padding:16 }}>{t("feed.detail.todayEmpty")}</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={adminThStyle}>{t("feed.detail.colLocation")}</th>
                  <th style={adminThStyle}>{t("feed.detail.colPlatform")}</th>
                  <th style={adminThStyle}>{t("feed.detail.colChannel")}</th>
                  <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.detail.colOrders")}</th>
                  <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.detail.colGross")}</th>
                </tr>
              </thead>
              <tbody>
                {c.todayByPlatform.map((r, i) => {
                  const w = wsById.get(r.workspaceId);
                  const name = channelName(r.workspaceId, r.channelId);
                  return (
                    <tr key={`${r.workspaceId}-${r.platformCode}-${i}`} style={rowBorder(i)}>
                      <td style={adminTdStyle}>{w ? (w.name || w.id) : "—"}</td>
                      <td style={adminTdStyle}><code style={codeStyle}>{r.platformCode || "—"}</code></td>
                      <td style={adminTdStyle}>{name || <span style={mutedText}>{t("feed.detail.unmappedChannel")}</span>}</td>
                      <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{r.orders}</td>
                      <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{fmtMoney(r.gross, w ? w.currency : null)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
